import React, { useEffect, useRef, useState } from 'react';
import dayjs, { Dayjs } from 'dayjs';
import { faChevronLeft, faChevronRight, faCalendar } from '@/ui';
import { Icon } from '@/ui/components';
import {
  ApplyDateButton,
  DayFilterBar,
  DayFilterButton,
  DayFilterIconButton,
  UpcomingCalendar,
  UpcomingCalendarDay,
  UpcomingCalendarGrid,
  UpcomingCalendarHeader,
  UpcomingCalendarNav,
  UpcomingCalendarWeekday,
} from '../LoadLaneStatus.styled';
import { UpcomingDayFilter } from '../utils';

const WEEKDAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

export const buildMonthGrid = (month: Dayjs): Dayjs[] => {
  const first = month.startOf('month');
  const lead = (first.day() + 6) % 7;
  const start = first.subtract(lead, 'day');
  const last = month.endOf('month');
  const tail = (7 - ((last.day() + 6) % 7) - 1 + 7) % 7;
  const end = last.add(tail, 'day');
  const days: Dayjs[] = [];
  let cursor = start;

  while (cursor.isBefore(end) || cursor.isSame(end, 'day')) {
    days.push(cursor);
    cursor = cursor.add(1, 'day');
  }

  return days;
};

const appliedDay = (value: UpcomingDayFilter) => {
  if (value === 'tomorrow') return dayjs().add(1, 'day').startOf('day');
  if (value === 'today') return dayjs().startOf('day');
  const parsed = dayjs(value.date);
  return parsed.isValid() ? parsed.startOf('day') : dayjs().startOf('day');
};

const toFilter = (day: Dayjs): UpcomingDayFilter => {
  const selected = day.startOf('day');
  if (selected.isSame(dayjs(), 'day')) return 'today';
  if (selected.isSame(dayjs().add(1, 'day'), 'day')) return 'tomorrow';
  return { date: selected.format('YYYY-MM-DD') };
};

type UpcomingDayPickerProps = {
  value: UpcomingDayFilter;
  onChange: (value: UpcomingDayFilter) => void;
};

export const UpcomingDayPicker: React.FC<UpcomingDayPickerProps> = ({
  value,
  onChange,
}) => {
  const rootRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState(() => appliedDay(value));
  const [visibleMonth, setVisibleMonth] = useState(() =>
    appliedDay(value).startOf('month'),
  );

  const isCustom = typeof value === 'object';

  const openCalendar = () => {
    const nextDraft = appliedDay(value);
    setDraft(nextDraft);
    setVisibleMonth(nextDraft.startOf('month'));
    setOpen(true);
  };

  useEffect(() => {
    if (!open) return undefined;

    const onPointerDown = (event: MouseEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) setOpen(false);
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };

    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  const days = buildMonthGrid(visibleMonth);

  return (
    <DayFilterBar ref={rootRef}>
      <DayFilterButton
        type="button"
        $active={value === 'today'}
        aria-pressed={value === 'today'}
        onClick={() => {
          setOpen(false);
          onChange('today');
        }}
      >
        Today
      </DayFilterButton>
      <DayFilterButton
        type="button"
        $active={value === 'tomorrow'}
        aria-pressed={value === 'tomorrow'}
        onClick={() => {
          setOpen(false);
          onChange('tomorrow');
        }}
      >
        Tomorrow
      </DayFilterButton>
      <DayFilterIconButton
        type="button"
        $active={open || isCustom}
        aria-label={
          isCustom
            ? `Choose date, ${appliedDay(value).format('D MMM YYYY')} selected`
            : 'Choose date'
        }
        aria-expanded={open}
        title={
          isCustom ? appliedDay(value).format('D MMM YYYY') : 'Choose date'
        }
        onClick={() => (open ? setOpen(false) : openCalendar())}
      >
        <Icon icon={faCalendar} />
      </DayFilterIconButton>
      {open ? (
        <UpcomingCalendar role="dialog" aria-label="Choose upcoming loads date">
          <UpcomingCalendarHeader>
            <UpcomingCalendarNav
              type="button"
              aria-label="Previous month"
              onClick={() =>
                setVisibleMonth((month) => month.subtract(1, 'month'))
              }
            >
              <Icon icon={faChevronLeft} />
            </UpcomingCalendarNav>
            {visibleMonth.format('MMMM YYYY')}
            <UpcomingCalendarNav
              type="button"
              aria-label="Next month"
              onClick={() => setVisibleMonth((month) => month.add(1, 'month'))}
            >
              <Icon icon={faChevronRight} />
            </UpcomingCalendarNav>
          </UpcomingCalendarHeader>
          <UpcomingCalendarGrid>
            {WEEKDAYS.map((weekday) => (
              <UpcomingCalendarWeekday key={weekday}>
                {weekday}
              </UpcomingCalendarWeekday>
            ))}
            {days.map((day) => {
              const selected = day.isSame(draft, 'day');
              return (
                <UpcomingCalendarDay
                  key={day.format('YYYY-MM-DD')}
                  type="button"
                  $outside={!day.isSame(visibleMonth, 'month')}
                  $selected={selected}
                  aria-label={day.format('D MMMM YYYY')}
                  aria-pressed={selected}
                  onClick={() => setDraft(day.startOf('day'))}
                >
                  {day.date()}
                </UpcomingCalendarDay>
              );
            })}
          </UpcomingCalendarGrid>
          <ApplyDateButton
            type="button"
            onClick={() => {
              onChange(toFilter(draft));
              setOpen(false);
            }}
          >
            Apply date
          </ApplyDateButton>
        </UpcomingCalendar>
      ) : null}
    </DayFilterBar>
  );
};
